import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: '複数プロダクトのUI・UXを統一、デザインシステム導入で一貫性実現へ',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview01.webp',
      alt: '左から、アイスリーデザインの佐々木、マクロミルのUX&プロダクトデザインユニット ユニット長の森本さん、アイスリーデザインの岩本、原田、今野',
      text: '左から、アイスリーデザインの佐々木、マクロミルのUX&プロダクトデザインユニット ユニット長の森本さん、アイスリーデザインの岩本、原田、今野',
    },
    {
      type: 'textBox',
      texts: ['―― 本プロジェクトが始まった経緯を教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '森本さん',
      texts: [
        <>
          <p>
            マクロミルは今年で25周年を迎えますが、創業当初はベンチャー企業として必要なサービスを急ピッチで構築してきました。
          </p>
          <p>
            しかし、システム部門にデザイン部署がなく、各事業部が独自にデザインコンペを行い、選んだデザイン会社に依頼する状況が続いていました。
            <span className={styles.textbold}>プロダクトごとにUIや操作体系が異なる</span>
            ため、ユーザーは各プロダクトの操作方法を覚える必要があり、
            <span className={styles.textbold}>学習コストやオペレーションコストの増大</span>
            を招いていました。また、それぞれのプロダクトのインフラやミドルレイヤー、デザインなどの管理システムが肥大化し、
            <span className={styles.textbold}>管理コスト</span>も増加していました。
          </p>
          <p>
            <span className={styles.space_top}>
              そこで、約3年前に就任した新CTOがエンジニアリングとデザインを内製化する体制を整備し、数百に上るシステムを共通プラットフォームに統合するプロジェクトを始動しました。
            </span>
          </p>
          <p>
            その流れの中で
            <span className={styles.textbold}>「せっかくやるならUXやUIも統一し、デザインガバナンスも強化しよう」</span>
            と、システム部門の中にデザイン組織を立ち上げ、デザインシステムのプロジェクトが始動したのです。私はそのデザイン組織の初メンバーとして、2023年7月に中途入社し、プロジェクトマネージャーを引き継ぎました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― デザインシステムの立ち上げにあたり、アイスリーデザイン側が実施したことはありますか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '佐々木',
      texts: [
        <>
          <p>
            弊社主導でワークショップを実施させていただきました。このワークショップの目的は、デザインシステムにおけるデザイン原則の策定です。
          </p>
          <p>
            デザインシステムでは、各コンポーネントや機能を都度更新するため、体験の一貫性を担保することが難しくなります。デザイン原則は、
            <span className={styles.textbold}>
              プロダクトのデザインに一貫性を持たせるための基本的な考えや指針を定め、デザインチームが意思決定する際の共通の基準
            </span>
            となります。同時に、ブランドの価値観やユーザー体験を支える役割も果たします。そのためワークショップは、執行役員様からプロダクトマネージャー様まで意思決定に関わる6人程のみなさまと弊社デザイナー陣で実施しました。
          </p>
          <p>
            <span className={styles.space_top}>
              その後、トーン＆マナー作成のためにブランドアーキタイプ分析も実施しました。これはマクロミルのユーザーであるマーケターやモニターの方を対象に設計を行いました。
            </span>
          </p>
          <p>
            具体的には各ユーザーに対して、どんなプロダクトが相応しいか、マクロミルの皆さんにアンケートを実施。そこから抽出されたキーワードを踏まえて、ムードボードを作成。ここからUIのビジュアル案に落とし込んでいきました。これにより、
            <span className={styles.textbold}>
              ブランドやプロダクトのイメージ（世界観）を統一し、視覚的に一貫性を持たせること
            </span>
            ができます。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― その後のデザインシステム構築プロジェクトはどのように進行していきましたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '岩本',
      texts: [
        <>
          <p>
            アイスリーデザイン側でデザインツール（Figmaなど）を使い、UI要素をコンポーネント化してデザインライブラリを作成しました。
          </p>
          <p>
            次に、マクロミルのご担当エンジニア様が、そのデザインコンポーネントをもとに、実際に動作するコードとしてコードライブラリを構築しました。コードライブラリの構築では、デザインで作成したガイドラインを参考に、ReactのUIフレームワークをベースに実装を進めていただきました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview02.webp',
      alt: 'Figmaで作成したデザインライブラリ',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <p>
            実装フェーズでは、デザイン通りに実装すると不具合が生じるポイントについて、
            <span className={styles.textbold}>
              ご担当エンジニア様と綿密にコミュニケーションを取り、ガイドラインや実装ルールの修正
            </span>
            を進めました。
          </p>
          <p>
            たとえば、「アクセシビリティの観点から文字サイズを調整した方が良い」といったフィードバックをいただき、デザインシステムのガイドラインや見た目、定義を随時アップデートする、といった形です。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview03.webp',
      alt: 'Figmaで作成したデザインライブラリ',
    },
    {
      type: 'greyBox',
      name: '今野',
      texts: [
        <>
          <p>
            森本さんが参画いただいたことで、弊社がデザインを作成し、森本さんのチームに承認いただくフローに変わりました。デザインシステムをマクロミル社内で活用いただくために、
            <span className={styles.textbold}>各デザインパーツの汎用性</span>について指摘をいただいたり、
            <span className={styles.textbold}>PMの方が画面設計を行うためのレイアウトパターンの追加</span>
            など、率先してディレクションいただけたことで、
            <span className={styles.textbold}>多角的な評価を基にデザインシステムの設計</span>を行うことができました。
          </p>
          <p>
            <span className={styles.space_top}>
              デザインシステムの社内普及にも奔走していただき、マクロミルさまの他プロジェクトもスピード感をもって進められています。デザインシステムの使用を前提にしているので、
              <span className={styles.textbold}>UI設計の議論よりも、体験設計の議論に集中</span>
              できるようになったと思います。
            </span>
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '膨大なUI要素から必要なものを厳選。使いやすさの鍵は「シンプルさ」',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview04.webp',
      alt: '膨大なUI要素から必要なものを厳選。使いやすさの鍵は「シンプルさ」',
    },
    {
      type: 'textBox',
      texts: ['―― 本プロジェクトの最大の課題はどこでしたか？'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '原田',
      texts: [
        <>
          <p>
            やはり、
            <span className={styles.textbold}>
              規模の大きいプロジェクトでしたので、それらをどう収束させるかが最大の課題
            </span>
            でした。各プロダクトのUI要素を洗い出しパーツを整理した上で、どこまでを取り入れるかの線引きを行いました。
          </p>
          <p>
            <span className={styles.textbold}>
              すべてを取り入れるとデザインシステムが肥大化してしまい、使いにくく管理コストも増大します。
            </span>
            そのため、シンプルに削ぎ落とすことを意識しました。削ぎ落とす際には、今後のプロダクトビジョンを共有していただきながら、「そのプロダクトにはどのコンポーネントが必要か」を検討。他社のデザインシステムも参考にしながら、必要な要素を選定していきました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview05.webp',
      alt: 'デザインシステムで必要な要素の選定',
    },
    {
      type: 'greyBox',
      name: '岩本',
      texts: [
        <>
          <p>
            そのあとは、各コンポーネントのビジュアルや動作を統一する作業を行いました。並行して、トンマナを確立するためのワークショップを開催したり、ヒアリングを行ったりしながら提案を行っていきました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview06.webp',
      alt: 'デザインシステムで各コンポーネントのビジュアルや動作を統一する作業',
    },
    {
      type: 'greyBox',
      name: '森本さん',
      texts: [
        <>
          <p>
            弊社には、広報・ブランディング系のデザイン部署や、クライアントワークを手掛けているデザイン部署など、さまざまなデザイン関係部署があります。ワークショップでは、そういった立場の違うデザイン関係者だけでなく、上層部も巻き込みながら、きちんと意見を集約して合意形成を進めていだきました。
          </p>
          <p>
            アウトプットを見て、
            <span className={styles.textbold}>プロセスをしっかり経て丁寧に進めていただいている</span>
            ことが伝わってきました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview07.webp',
      alt: 'デザインシステムのインタビュー写真',
    },
    {
      type: 'textBox',
      texts: [
        '―― 現在はデザインシステムの運用フェーズに移行していますが、運用時に意識していることがあれば教えてください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '岩本',
      texts: [
        <>
          <p>
            <span className={styles.textbold}>客観的な視点を保つこと</span>です。
          </p>
          <p>
            現在、私と原田が新しいプロダクトを作成するプロジェクトに参加し、実際にデザインシステムを使いながら必要に応じて改善提案を行い、アップデートを行っています。個別のプロジェクトを進める際は、常に「デザインシステム全体として最適かどうか」を考えています。
          </p>
          <p>
            <span className={styles.textbold}>
              デザインシステムは、複数のプロダクトやプロジェクトを横断して活用されるもの
            </span>
            です。そのため、
            <span className={styles.textbold}>
              特定のプロダクト視点に偏ると、システム全体の汎用性を損なうリスクがある
            </span>
            からです。具体的には、他社のデザインシステムや多様なプロダクトの事例を調査し、その知見を取り入れることで、システムの汎用性を高めるよう努めています。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: [
        '――  今回のプロジェクトで特に印象に残った点や、お役に立てた部分などがございましたら、ぜひお聞かせください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '森本さん',
      texts: [
        <>
          <p>
            デザインシステムの導入・運用はマクロミルにとって新しい取り組みでした。そのため、さまざまな企業のデザインシステムに触れてきた外部パートナーの知識や実績を活かし、伴走しながら設計・開発してもらえたことは大きな助けになりました。特に、社内のステークホルダーへ説明する際、外部の客観的な意見があることで説得力が格段に増したと感じています。
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'プロトタイプ作成時間が半減し、懐疑的だった社内からも好評価の声',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview08.webp',
      alt: 'プロトタイプ作成時間が半減し、懐疑的だった社内からも好評価の声',
    },
    {
      type: 'textBox',
      texts: ['―― 今回のデザインシステムの特徴について教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '佐々木',
      texts: [
        <>
          <p>
            <span className={styles.textbold}>3種類のアクター（ユーザータイプ）に対してテーマ設定を行っている点</span>
            です。
          </p>
          <p>
            この3種類のアクターとは、①社内でアンケート設計やリサーチを行う
            <span className={styles.textbold}>マクロミルのメンバー</span>、②マクロミルに調査を依頼する
            <span className={styles.textbold}>クライアント</span>、③アンケートに回答する
            <span className={styles.textbold}>一般ユーザー</span>（モニター）のことです。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview09.webp',
      alt: '1Member 2Client 3Monitor',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <p>
            各アクターごとに、デザインのテイストやUIの考え方を調整しました。たとえば、社内メンバーは主にPCを使用するため、デザインはPC向けのUIを前提としています。一方、一般ユーザーはスマートフォンでアンケートに回答することが多いため、スマートフォン向けのUIに特に注力しました。
          </p>
          <p>
            このように、
            <span className={styles.textbold}>
              それぞれの利用シーンに最適化したデザインを提供することで、ユーザーにとって使いやすいシステムを実現
            </span>
            しています。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― 効果を感じたエピソードがあればお伺いさせてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '森本さん',
      texts: [
        <>
          <p>
            デザインシステムの導入で、<span className={styles.textbold}>プロトタイプ作成のスピードが飛躍的に向上</span>
            しました。システムにはデザイナー向けのガイドラインや多様なデザインパーツが揃っているので、「この機能を作りましょう」となった際、
            <span className={styles.textbold}>
              レゴブロックのようにパーツを組み合わせ、少しアニメーションを加えるだけで、プロトタイプを素早く構築可能
            </span>
            です。
          </p>
          <p>
            そのおかげで、1か月かかっていたのが2週間に短縮されるなど、認識合わせのスピードも大幅にアップしました。特に、初めてデザインシステムを使うプロダクトマネージャーは「これまでにないスピード感を感じた」と、効果を実感してくれているようです。
          </p>
          <p>
            これまではデザインシステムの必要性や目的をいくら説明しても、経験のない人には伝わりにくかったようですが、この実績を通して価値や意図を理解してもらいやすくなったと感じています。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/macromill-designsystem/interview10.webp',
      alt: 'デザインシステムのインタビュー写真',
    },
    {
      type: 'textBox',
      texts: ['―― 今後の目標について教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '森本さん',
      texts: [
        <>
          <p>
            現在のデザインシステムは、当時のトレンドや最新のデザインツール（たとえば、Figmaの新機能）を活用して構築され、一定の品質水準を維持しています。実際に他社でも導入されるレベルの完成度で、現在も問題なく運用中です。
          </p>
          <p>
            一方で、来年度（2025年7月以降）に向け、
            <span className={styles.textbold}>より特徴的な機能を追加する計画を進行中</span>
            です。アイスリーデザインさんとも既に企画段階で協議を進め、世界的にも未実現の機能を目指しています。成功すればユニークで革新的なデザインシステムとして、他社にはない強みを打ち出せると考えています。
          </p>
        </>,
      ],
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
